import { BufferGeometry, DynamicDrawUsage, Float32BufferAttribute } from 'three/webgpu';
import { PARTICLE_STRIDE, P_BIRTH, P_CENTER, P_LIFETIME, P_TINT } from './material.js';
/**
 * Trail ribbons (plan §3.1): a ring buffer of past positions per particle,
 * flattened into one dynamic BufferGeometry per layer. The CPU writes history
 * points + tangents each frame; the vertex stage (buildTrailMaterial) expands
 * each point into a camera-facing strip.
 *
 * Vertex layout: capacity × points × 2 (left/right side), indexed as strips.
 */
export class TrailBuffers {
    geometry;
    capacity;
    points;
    minDistance;
    /** capacity × points × 3, ring-ordered. */
    history;
    head;
    started;
    constructor(capacity, points, minDistance = 0) {
        this.capacity = capacity;
        this.points = Math.max(2, points);
        this.minDistance = minDistance;
        this.history = new Float32Array(capacity * this.points * 3);
        this.head = new Uint16Array(capacity);
        this.started = new Uint8Array(capacity);
        const vertCount = capacity * this.points * 2;
        const geo = new BufferGeometry();
        const dyn = (itemSize, name) => {
            const attr = new Float32BufferAttribute(new Float32Array(vertCount * itemSize), itemSize);
            attr.setUsage(DynamicDrawUsage);
            geo.setAttribute(name, attr);
            return attr;
        };
        dyn(3, 'position');
        dyn(3, 'trailTangent');
        dyn(3, 'trailInfo'); // (trailT, nAge, alive)
        dyn(4, 'trailTint');
        const sides = new Float32Array(vertCount);
        for (let v = 0; v < vertCount; v++)
            sides[v] = v % 2 === 0 ? -1 : 1;
        geo.setAttribute('trailSide', new Float32BufferAttribute(sides, 1));
        const indices = [];
        for (let i = 0; i < capacity; i++) {
            const base = i * this.points * 2;
            for (let j = 0; j < this.points - 1; j++) {
                const a = base + j * 2;
                indices.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
            }
        }
        geo.setIndex(indices);
        this.geometry = geo;
    }
    reset() {
        this.started.fill(0);
        this.head.fill(0);
    }
    /** Prime a particle's ring with its spawn position (ribbon starts collapsed). */
    onSpawn(i, x, y, z) {
        const base = i * this.points * 3;
        for (let j = 0; j < this.points; j++) {
            this.history[base + j * 3] = x;
            this.history[base + j * 3 + 1] = y;
            this.history[base + j * 3 + 2] = z;
        }
        this.head[i] = 0;
        this.started[i] = 1;
    }
    /**
     * Push current positions into the rings and rewrite the vertex buffers.
     * Called by LayerRuntime after integration + spawning each frame. Reads
     * straight from the interleaved particle buffer.
     */
    update(attrs, time) {
        const a = attrs.array;
        const P = this.points;
        const posAttr = this.geometry.getAttribute('position');
        const tanAttr = this.geometry.getAttribute('trailTangent');
        const infoAttr = this.geometry.getAttribute('trailInfo');
        const tintAttr = this.geometry.getAttribute('trailTint');
        const outPos = posAttr.array;
        const outTan = tanAttr.array;
        const outInfo = infoAttr.array;
        const outTint = tintAttr.array;
        for (let i = 0; i < this.capacity; i++) {
            const rec = i * PARTICLE_STRIDE;
            const birthI = a[rec + P_BIRTH];
            const alive = birthI >= 0 && this.started[i] === 1;
            const ringBase = i * P * 3;
            if (alive) {
                // advance the ring head unless we haven't moved far enough
                const h = this.head[i];
                const hx = this.history[ringBase + h * 3];
                const hy = this.history[ringBase + h * 3 + 1];
                const hz = this.history[ringBase + h * 3 + 2];
                const px = a[rec + P_CENTER];
                const py = a[rec + P_CENTER + 1];
                const pz = a[rec + P_CENTER + 2];
                const moved = Math.hypot(px - hx, py - hy, pz - hz);
                const h2 = moved >= this.minDistance ? (h + 1) % P : h;
                this.history[ringBase + h2 * 3] = px;
                this.history[ringBase + h2 * 3 + 1] = py;
                this.history[ringBase + h2 * 3 + 2] = pz;
                this.head[i] = h2;
            }
            const nAge = alive ? Math.min(1, (time - birthI) / Math.max(1e-6, a[rec + P_LIFETIME])) : 1;
            const head = this.head[i];
            const vBase = i * P * 2;
            for (let j = 0; j < P; j++) {
                // j = 0 newest … P-1 oldest
                const ring = (head - j + P * 2) % P;
                const ringPrev = (head - Math.max(0, j - 1) + P * 2) % P; // toward newer
                const ringNext = (head - Math.min(P - 1, j + 1) + P * 2) % P; // toward older
                const x = this.history[ringBase + ring * 3];
                const y = this.history[ringBase + ring * 3 + 1];
                const z = this.history[ringBase + ring * 3 + 2];
                let tx = this.history[ringBase + ringPrev * 3] - this.history[ringBase + ringNext * 3];
                let ty = this.history[ringBase + ringPrev * 3 + 1] - this.history[ringBase + ringNext * 3 + 1];
                let tz = this.history[ringBase + ringPrev * 3 + 2] - this.history[ringBase + ringNext * 3 + 2];
                if (tx * tx + ty * ty + tz * tz < 1e-12)
                    tx = 1; // degenerate (not yet moved)
                const trailT = P > 1 ? j / (P - 1) : 0;
                for (let s = 0; s < 2; s++) {
                    const v = (vBase + j * 2 + s) * 3;
                    outPos[v] = x;
                    outPos[v + 1] = y;
                    outPos[v + 2] = z;
                    outTan[v] = tx;
                    outTan[v + 1] = ty;
                    outTan[v + 2] = tz;
                    outInfo[v] = trailT;
                    outInfo[v + 1] = nAge;
                    outInfo[v + 2] = alive ? 1 : 0;
                    const t4 = (vBase + j * 2 + s) * 4;
                    outTint[t4] = a[rec + P_TINT];
                    outTint[t4 + 1] = a[rec + P_TINT + 1];
                    outTint[t4 + 2] = a[rec + P_TINT + 2];
                    outTint[t4 + 3] = a[rec + P_TINT + 3];
                }
            }
        }
        posAttr.needsUpdate = true;
        tanAttr.needsUpdate = true;
        infoAttr.needsUpdate = true;
        tintAttr.needsUpdate = true;
    }
}
